import { useState, useEffect, useMemo } from 'react';
import { capacidadesAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2, Plus, Trash2, Info } from 'lucide-react';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

/**
 * El criterio con el que actúa un agente: **prosa, y nada más que prosa**.
 *
 * De los once documentos del catálogo de skills, ocho no declaran ni una herramienta —son prosa—
 * pero venían mezclados con el interruptor de una capacidad del producto, las herramientas de un
 * sistema externo y sus credenciales. Por eso «skill» terminó significando cuatro cosas. Un
 * criterio es sólo la primera: **cuándo actuar y con qué cuidado**.
 *
 * **Los tres alcances se suman, no se pisan**, y la pantalla lo dice: el de la organización, el
 * del área y el del agente son verdaderos al mismo tiempo. Es la única precedencia distinta del
 * resto del producto, y esconderla haría que alguien escriba el del área creyendo que reemplaza
 * al de la casa.
 *
 * **Lo que un criterio no puede tener**, y la pantalla lo recuerda porque es la mitad del diseño:
 * herramientas, credenciales, y descripción del motor. Un texto que diga *«el tamaño máximo es 12
 * MB»* miente el día que el límite cambie.
 */
export default function Criterios({ criterios, onGuardar, guardando, alcance = 'capsula' }) {
  const [borrador, setBorrador] = useState([]);
  const [capacidades, setCapacidades] = useState([]);
  const [confirmar, setConfirmar] = useState({ open: false });

  useEffect(() => { setBorrador(criterios || []); }, [criterios]);
  useEffect(() => {
    capacidadesAPI.delProducto()
      .then(({ data }) => setCapacidades(data || []))
      .catch(() => setCapacidades([]));
  }, []);

  const sucio = useMemo(
    () => JSON.stringify(borrador) !== JSON.stringify(criterios || []),
    [borrador, criterios],
  );

  const cambiar = (id, campo, valor) =>
    setBorrador((prev) => prev.map((c) => (c.criterio_id === id ? { ...c, [campo]: valor } : c)));

  const agregar = () =>
    setBorrador((prev) => [...prev, {
      criterio_id: `criterio-${Date.now()}`,
      titulo: '',
      texto: '',
      capacidad_id: '',
      activa: true,
    }]);

  const borrar = (c) => setConfirmar({
    open: true,
    title: 'Borrar este criterio',
    description: `Se va a borrar «${c.titulo || c.criterio_id}». Los agentes dejan de recibirlo en el próximo turno.`,
    confirmText: 'Borrarlo',
    onConfirm: () => setBorrador((prev) => prev.filter((x) => x.criterio_id !== c.criterio_id)),
  });

  const dondeManda = {
    tenant: 'toda la organización',
    capsula: 'esta cápsula',
    agente: 'este agente',
  }[alcance] || 'este alcance';

  return (
    <div className="space-y-3" data-testid="criterios">
      <ConfirmActionDialog
        {...confirmar}
        onOpenChange={(open) => setConfirmar((p) => ({ ...p, open }))}
      />

      <QueSeConfiguraAca
        testId="ayuda-criterios"
        queEs="Prosa que le dice a un agente cuándo actuar y con qué cuidado. Nada más que prosa: no lleva herramientas, ni credenciales, ni explica cómo funciona el sistema."
        paraQue="Es el conocimiento del área escrito una vez. «Ante una incidencia del ERP, priorizá los objetos Apex»: eso no es una herramienta ni un parámetro, es criterio — y es lo que hace que un agente trabaje como trabaja este equipo."
        aQueAfecta={<>Entra en el prompt de los agentes <b>en cada turno</b>. Cuanto más escribas, más contexto se paga por mensaje: conviene que sea corto y que diga algo que cambie una decisión.</>}
        ejemplo={<><b>Los tres alcances se suman, no se pisan</b> — y es la única precedencia distinta del resto del producto. El criterio de la organización, el del área y el del agente son verdaderos <b>al mismo tiempo</b>: el del área no reemplaza al de la casa, se agrega.</>}
        ojo={<>Un criterio que dice «el tamaño máximo es 12 MB» <b>miente el día que el límite cambie</b>. Los números son parámetros, no prosa. Y si un criterio aplica sólo a una capacidad, el agente que no la tenga prendida no lo recibe.</>}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="max-w-2xl space-y-1">
          <p className="text-[10px] text-zinc-500">
            Cuándo actuar y con qué cuidado, para <b>{dondeManda}</b>. Es prosa: no lleva
            herramientas, ni credenciales, ni explica cómo funciona el sistema.
          </p>
          <p className="text-[10px] text-zinc-500 flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              Los criterios de la organización, del área y del agente <b>se suman</b> — no se
              pisan. El agente los lee en ese orden, del marco general al matiz.
            </span>
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={agregar} data-testid="agregar-criterio"
                className="h-7 text-[10px] rounded-sm gap-1 shrink-0">
          <Plus className="w-3 h-3" /> Agregar
        </Button>
      </div>

      {!borrador.length && (
        <Card className="rounded-sm border-dashed border-brand-border">
          <CardContent className="p-5 text-center">
            <p className="text-xs text-zinc-500">Todavía no hay ningún criterio escrito acá.</p>
            <p className="text-[10px] text-zinc-400 mt-1">
              Sin criterios, el agente actúa sólo con su SOUL y sus RULES.
            </p>
          </CardContent>
        </Card>
      )}

      {borrador.map((c) => (
        <Card key={c.criterio_id}
              className={`rounded-sm ${c.activa === false ? 'border-brand-border bg-zinc-50/50' : 'border-brand-border'}`}>
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Switch
                data-testid={`criterio-activo-${c.criterio_id}`}
                checked={c.activa !== false}
                onCheckedChange={(v) => cambiar(c.criterio_id, 'activa', v)}
              />
              <Input
                data-testid={`criterio-titulo-${c.criterio_id}`}
                value={c.titulo || ''}
                onChange={(e) => cambiar(c.criterio_id, 'titulo', e.target.value)}
                placeholder="Título — lo que se lee acá y encabeza el criterio"
                className="rounded-sm text-xs h-7 flex-1"
              />
              <select
                data-testid={`criterio-capacidad-${c.criterio_id}`}
                value={c.capacidad_id || ''}
                onChange={(e) => cambiar(c.criterio_id, 'capacidad_id', e.target.value)}
                className="h-7 text-[10px] border border-brand-border rounded-sm px-1 bg-white max-w-[190px]"
                title="Si aplica sólo a una capacidad, el agente que no la tenga prendida no lo recibe"
              >
                <option value="">Vale siempre</option>
                {capacidades.map((cap) => (
                  <option key={cap.capacidad_id} value={cap.capacidad_id}>
                    Sólo al {cap.nombre.toLowerCase()}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => borrar(c)}
                      data-testid={`borrar-criterio-${c.criterio_id}`}
                      className="text-zinc-300 hover:text-red-600" title="Borrar">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <textarea
              data-testid={`criterio-texto-${c.criterio_id}`}
              value={c.texto || ''}
              onChange={(e) => cambiar(c.criterio_id, 'texto', e.target.value)}
              placeholder="Ante una incidencia del ERP, priorizá los objetos Apex y expandí su impacto antes de concluir."
              className="w-full border border-brand-border rounded-sm p-2 text-[11px] h-20 focus:ring-1 focus:ring-brand-primary focus:outline-none resize-y"
            />
            {c.capacidad_id && (
              <Badge variant="outline" className="text-[9px] rounded-sm px-1 py-0">
                un agente sin esa capacidad prendida no lo recibe
              </Badge>
            )}
          </CardContent>
        </Card>
      ))}

      <Button
        data-testid="guardar-criterios"
        onClick={() => onGuardar(borrador)}
        disabled={guardando || !sucio}
        className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-8"
      >
        {guardando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
        Guardar criterio
      </Button>
    </div>
  );
}
